import { useEffect, useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import type { AdminUser } from '@wlb/shared/api/admin/adminTypes';
import { REGEX } from '@wlb/shared/constants/regex';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import { TextField } from '../../components/ui/TextField';

const MIN_PASSWORD_LENGTH = 8;

interface AdminAccountDialogProps {
  open: boolean;
  /** Null opens the dialog in create mode. */
  account: AdminUser | null;
  onClose: () => void;
  onSaved: () => void;
}

interface Fields {
  firstName: string;
  userName: string;
  email: string;
  password: string;
}

const EMPTY_FIELDS: Fields = {
  firstName: '',
  userName: '',
  email: '',
  password: '',
};

export function AdminAccountDialog({
  open,
  account,
  onClose,
  onSaved,
}: AdminAccountDialogProps) {
  const { t } = useTranslation('admin');
  const isEdit = account != null;
  const [fields, setFields] = useState<Fields>(EMPTY_FIELDS);
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFields(
      account
        ? {
            firstName: account.firstName,
            userName: account.userName,
            email: account.email,
            password: '',
          }
        : EMPTY_FIELDS,
    );
    setErrors({});
    setSubmitError(null);
  }, [account, open]);

  const validate = (): boolean => {
    const next: Partial<Record<keyof Fields, string>> = {};
    if (!fields.firstName.trim()) {
      next.firstName = t('adminAccounts.form.firstNameRequired');
    }
    if (!fields.userName.trim()) {
      next.userName = t('adminAccounts.form.userNameRequired');
    }
    if (!fields.email.trim()) {
      next.email = t('adminAccounts.form.emailRequired');
    } else if (!REGEX.EMAIL.test(fields.email.trim())) {
      next.email = t('adminAccounts.form.emailInvalid');
    }
    if (!isEdit && !fields.password) {
      next.password = t('adminAccounts.form.passwordRequired');
    } else if (fields.password && fields.password.length < MIN_PASSWORD_LENGTH) {
      next.password = t('adminAccounts.form.passwordTooShort');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = {
        firstName: fields.firstName.trim(),
        userName: fields.userName.trim(),
        email: fields.email.trim(),
        ...(fields.password ? { password: fields.password } : {}),
      };
      if (isEdit && account) {
        await adminService.updateAdminAccount(account.userGuid, payload);
        ToastService.show({
          type: 'success',
          message: t('adminAccounts.toasts.updated'),
        });
      } else {
        await adminService.createAdminAccount(payload);
        ToastService.show({
          type: 'success',
          message: t('adminAccounts.toasts.created'),
        });
      }
      onSaved();
      onClose();
    } catch (cause) {
      setSubmitError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setSubmitting(false);
    }
  };

  const update = (key: keyof Fields, value: string) => {
    setFields(current => ({ ...current, [key]: value }));
  };

  return (
    <Modal
      open={open}
      title={t(isEdit ? 'adminAccounts.form.editTitle' : 'adminAccounts.form.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting
              ? t('common.saving')
              : t(isEdit ? 'adminAccounts.form.submitEdit' : 'adminAccounts.form.submitCreate')}
          </Button>
        </>
      }>
      <FormError variant="banner" message={submitError} />
      <TextField
        label={t('adminAccounts.form.firstName')}
        value={fields.firstName}
        error={errors.firstName}
        onChange={event => update('firstName', event.target.value)}
      />
      <TextField
        label={t('adminAccounts.form.userName')}
        value={fields.userName}
        error={errors.userName}
        autoComplete="off"
        onChange={event => update('userName', event.target.value)}
      />
      <TextField
        label={t('adminAccounts.form.email')}
        type="email"
        value={fields.email}
        error={errors.email}
        onChange={event => update('email', event.target.value)}
      />
      <TextField
        label={t(isEdit ? 'adminAccounts.form.passwordOptional' : 'adminAccounts.form.password')}
        type="password"
        autoComplete="new-password"
        value={fields.password}
        error={errors.password}
        onChange={event => update('password', event.target.value)}
      />
    </Modal>
  );
}
